import { useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  CanAccess,
  type Identifier,
  useDataProvider,
  useNotify,
  useTranslate,
} from "ra-core";
import { Navigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import { StatusPill } from "../design/StatusPill";
import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import { MobileBackButton } from "../misc/MobileBackButton";
import type { CrmDataProvider } from "../providers/types";
import type { MetaCapiEventSummary, MetaCapiLogEntry } from "../types";
import {
  EMPTY_META_EVENTS_FILTERS,
  META_EVENTS_PAGE_SIZE,
  META_EVENT_NAMES,
  META_EVENT_STATUSES,
  type MetaEventsFilterState,
  buildMetaEventFilters,
  eventNameKey,
  eventStatusKey,
  formatPayload,
  hasMissingConfig,
  isRetryable,
  isValidEventsRange,
  maskSensitiveText,
  processedCount,
} from "./metaEventsRules";

const QUERY_KEY = "meta_capi_events";
const LOG_QUERY_KEY = "meta_capi_log";
const SELECT_CLASS =
  "h-11 w-full rounded-md border bg-background px-3 text-sm sm:h-9";
const MEXICO_TIME_ZONE = "America/Mexico_City";
const EMPTY = "-";

const formatInstant = (value: string | null | undefined): string =>
  value
    ? new Date(value).toLocaleString("es-MX", {
        timeZone: MEXICO_TIME_ZONE,
        dateStyle: "short",
        timeStyle: "short",
      })
    : EMPTY;

const formatValue = (event: MetaCapiEventSummary): string =>
  event.event_name === "compra" && event.value != null
    ? `${event.value.toLocaleString("es-MX")} ${event.currency}`
    : EMPTY;

/**
 * Almikia Epic EM (TASK-069): direccion-only "Eventos Meta" screen, reached from Settings. The
 * Conversions API outbox with its latest status, an attempt log loaded on expand, "Reintentar"
 * for failed / abandoned events and "Procesar pendientes". Stored payloads are already free of
 * personal data and are masked again on display. Errors are translated, never the raw message.
 * RLS and the edge functions are the real authority; `CanAccess` only sends other roles home.
 */
export const MetaEventsScreen = () => (
  <CanAccess
    resource="meta_capi_events_summary"
    action="list"
    accessDenied={<Navigate to="/" replace />}
  >
    <MetaEventsContent />
  </CanAccess>
);

MetaEventsScreen.path = "/meta_events";

const MetaEventsContent = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [filters, setFilters] = useState<MetaEventsFilterState>(
    EMPTY_META_EVENTS_FILTERS,
  );
  const [retryingId, setRetryingId] = useState<Identifier | null>(null);
  const rangeIsValid = isValidEventsRange(filters.from, filters.to);

  const events = useQuery({
    queryKey: [QUERY_KEY, filters],
    enabled: rangeIsValid,
    placeholderData: keepPreviousData,
    queryFn: () =>
      dataProvider.getMetaCapiEvents(buildMetaEventFilters(filters)),
  });
  const rows = events.data?.data ?? [];
  const total = events.data?.total ?? 0;
  const lastPage = Math.max(Math.ceil(total / META_EVENTS_PAGE_SIZE), 1);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
  const onFailure = (error: unknown) => {
    console.error("metaEvents.action.error", error);
    notify("crm.metaOrigin.events.action_error", { type: "error" });
  };

  const retry = useMutation({
    mutationFn: (id: Identifier) => dataProvider.retryMetaCapiEvent(id),
    onMutate: (id) => setRetryingId(id),
    onSuccess: () => {
      notify("crm.metaOrigin.events.retry_done", { type: "success" });
      return refresh();
    },
    onError: onFailure,
    onSettled: () => setRetryingId(null),
  });

  const processPending = useMutation({
    mutationFn: async () => {
      // Failed or abandoned events also leave "pending", so count what reached "sent".
      const sentTotal = async () =>
        (
          await dataProvider.getMetaCapiEvents({
            status: "sent",
            page: 1,
            perPage: 1,
          })
        ).total;
      const before = await sentTotal();
      await dataProvider.processPendingMetaCapi();
      return processedCount(before, await sentTotal());
    },
    onSuccess: (count) => {
      notify("crm.metaOrigin.events.process_pending_result", {
        type: "success",
        messageArgs: { count },
      });
      return refresh();
    },
    onError: onFailure,
  });

  const setFilter = (patch: Partial<MetaEventsFilterState>) =>
    setFilters((current) => ({ ...current, page: 1, ...patch }));

  const title = translate("crm.metaOrigin.events.title");
  const content = (
    <>
      <Card>
        <CardContent className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <Label htmlFor="meta-events-status">
              {translate("crm.metaOrigin.events.filter_status")}
            </Label>
            <select
              id="meta-events-status"
              className={SELECT_CLASS}
              value={filters.status}
              onChange={(event) =>
                setFilter({
                  status: event.target.value as MetaEventsFilterState["status"],
                })
              }
            >
              <option value="">
                {translate("crm.metaOrigin.events.filter_all")}
              </option>
              {META_EVENT_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {translate(eventStatusKey(value))}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="meta-events-name">
              {translate("crm.metaOrigin.events.filter_event")}
            </Label>
            <select
              id="meta-events-name"
              className={SELECT_CLASS}
              value={filters.eventName}
              onChange={(event) =>
                setFilter({
                  eventName: event.target
                    .value as MetaEventsFilterState["eventName"],
                })
              }
            >
              <option value="">
                {translate("crm.metaOrigin.events.filter_all")}
              </option>
              {META_EVENT_NAMES.map((value) => (
                <option key={value} value={value}>
                  {translate(eventNameKey(value))}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="meta-events-from">
              {translate("crm.metaOrigin.events.filter_from")}
            </Label>
            <Input
              id="meta-events-from"
              type="date"
              className="h-11 sm:h-9"
              value={filters.from}
              onChange={(event) => setFilter({ from: event.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="meta-events-to">
              {translate("crm.metaOrigin.events.filter_to")}
            </Label>
            <Input
              id="meta-events-to"
              type="date"
              className="h-11 sm:h-9"
              value={filters.to}
              onChange={(event) => setFilter({ to: event.target.value })}
            />
          </div>
        </CardContent>
      </Card>

      {hasMissingConfig(rows) ? (
        <div
          role="status"
          className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <p className="font-medium">
            {translate("crm.metaOrigin.events.config_missing_title")}
          </p>
          <p>{translate("crm.metaOrigin.events.config_missing_body")}</p>
        </div>
      ) : null}

      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          className="h-11 sm:h-9"
          disabled={processPending.isPending}
          onClick={() => processPending.mutate()}
        >
          {translate("crm.metaOrigin.events.process_pending_action")}
        </Button>
      </div>

      {events.error ? (
        <ListErrorState
          message={translate("crm.metaOrigin.events.load_error")}
          onRetry={() => events.refetch()}
        />
      ) : !rangeIsValid ? (
        <ListEmptyState
          className="min-h-0 py-4"
          title={translate("crm.metaOrigin.events.empty")}
        />
      ) : !events.data ? (
        <ListCardsSkeleton rows={3} />
      ) : rows.length === 0 ? (
        <ListEmptyState
          className="min-h-0 py-4"
          title={translate("crm.metaOrigin.events.empty")}
        />
      ) : (
        <>
          <ul className="space-y-2" data-testid="meta-events-list">
            {rows.map((row) => (
              <li key={row.id}>
                <EventCard
                  event={row}
                  isRetrying={retryingId === row.id}
                  onRetry={() => retry.mutate(row.id)}
                />
              </li>
            ))}
          </ul>
          {lastPage > 1 ? (
            <div className="flex items-center justify-between gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-11 sm:h-9"
                disabled={filters.page <= 1}
                onClick={() =>
                  setFilters((current) => ({
                    ...current,
                    page: current.page - 1,
                  }))
                }
              >
                {translate("ra.navigation.previous")}
              </Button>
              <span className="text-sm text-muted-foreground">
                {filters.page} / {lastPage}
              </span>
              <Button
                type="button"
                variant="outline"
                className="h-11 sm:h-9"
                disabled={filters.page >= lastPage}
                onClick={() =>
                  setFilters((current) => ({
                    ...current,
                    page: current.page + 1,
                  }))
                }
              >
                {translate("ra.navigation.next")}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <MobileBackButton to="/" />
          <div className="flex min-w-0 flex-1">
            <h1 className="truncate text-xl font-semibold">{title}</h1>
          </div>
        </MobileHeader>
        <MobileContent>
          <div className="space-y-4">{content}</div>
        </MobileContent>
      </>
    );
  }
  return (
    <div className="mx-auto mt-2 max-w-4xl space-y-4 pb-8">
      <h1 className="px-1 text-xl font-semibold">{title}</h1>
      {content}
    </div>
  );
};

type EventCardProps = {
  event: MetaCapiEventSummary;
  isRetrying: boolean;
  onRetry: () => void;
};

const EventCard = ({ event, isRetrying, onRetry }: EventCardProps) => {
  const translate = useTranslate();
  const [isOpen, setIsOpen] = useState(false);
  const statusText = translate(eventStatusKey(event.status));
  const fields: [string, string][] = [
    ["value", formatValue(event)],
    ["attempts", String(event.attempts)],
    ["event_time", formatInstant(event.event_time)],
    [
      "next_attempt",
      event.status === "pending" ? formatInstant(event.next_attempt_at) : EMPTY,
    ],
    [
      "last_error",
      event.last_error ? maskSensitiveText(event.last_error) : EMPTY,
    ],
  ];
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="break-words font-medium">
              {translate(eventNameKey(event.event_name))}
            </p>
            <p className="break-words text-sm text-muted-foreground">
              {event.lead_label}
            </p>
          </div>
          {event.status === "sent" ? (
            <StatusPill tone="ok" text={statusText} />
          ) : event.status === "pending" ? (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
              {statusText}
            </span>
          ) : (
            <StatusPill tone="not-ok" text={statusText} />
          )}
        </div>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm sm:grid-cols-3">
          {fields.map(([key, value]) => (
            <div key={key} className="min-w-0">
              <dt className="text-xs text-muted-foreground">
                {translate(`crm.metaOrigin.events.headers.${key}`)}
              </dt>
              <dd className="break-words tabular-nums">{value}</dd>
            </div>
          ))}
        </dl>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-11 sm:h-9"
            aria-expanded={isOpen}
            onClick={() => setIsOpen((open) => !open)}
          >
            {translate(
              isOpen
                ? "crm.metaOrigin.events.log.hide"
                : "crm.metaOrigin.events.log.show",
            )}
          </Button>
          {isRetryable(event) ? (
            <Button
              type="button"
              size="sm"
              className="h-11 sm:h-9"
              disabled={isRetrying}
              onClick={onRetry}
            >
              {translate("crm.metaOrigin.events.retry_action")}
            </Button>
          ) : null}
        </div>
        {isOpen ? <EventLog outboxId={event.id} /> : null}
      </CardContent>
    </Card>
  );
};

const EventLog = ({ outboxId }: { outboxId: Identifier }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const log = useQuery({
    queryKey: [LOG_QUERY_KEY, outboxId],
    queryFn: () => dataProvider.getMetaCapiLog(outboxId, { perPage: 100 }),
  });
  if (log.error) {
    return (
      <ListErrorState
        message={translate("crm.metaOrigin.events.log.error")}
        onRetry={() => log.refetch()}
      />
    );
  }
  if (!log.data) {
    return <ListCardsSkeleton rows={1} />;
  }
  if (log.data.data.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {translate("crm.metaOrigin.events.log.empty")}
      </p>
    );
  }
  // The provider returns newest first; the log reads in attempt order.
  const entries = [...log.data.data].reverse();
  return (
    <ol className="space-y-2 border-t pt-3" data-testid="meta-event-log">
      {entries.map((entry) => (
        <LogEntry key={entry.id} entry={entry} />
      ))}
    </ol>
  );
};

const LogEntry = ({ entry }: { entry: MetaCapiLogEntry }) => {
  const translate = useTranslate();
  const payload = formatPayload(entry.payload);
  return (
    <li className="space-y-1 rounded-md bg-muted/40 p-2 text-sm">
      <p className="font-medium">
        {translate("crm.metaOrigin.events.log.attempt", {
          number: entry.attempt_number,
        })}{" "}
        · {translate(`crm.metaOrigin.events.log_status.${entry.status}`)} ·{" "}
        <span className="font-normal text-muted-foreground">
          {formatInstant(entry.created_at)}
        </span>
      </p>
      {entry.http_status != null ? (
        <p>
          {translate("crm.metaOrigin.events.log.http_status")}:{" "}
          {entry.http_status}
        </p>
      ) : null}
      {entry.response_body ? (
        <p className="break-words">
          {translate("crm.metaOrigin.events.log.response")}:{" "}
          {maskSensitiveText(entry.response_body)}
        </p>
      ) : null}
      {entry.error ? (
        <p className="break-words">
          {translate("crm.metaOrigin.events.log.error_label")}:{" "}
          {maskSensitiveText(entry.error)}
        </p>
      ) : null}
      {payload ? (
        <pre className="max-w-full overflow-x-auto whitespace-pre-wrap break-words rounded bg-background p-2 text-xs">
          {payload}
        </pre>
      ) : null}
    </li>
  );
};
